import { useState, useRef, useEffect, useCallback } from "react";
import { Loader2, Send, AtSign, User } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { CollaboratorUserInfo } from "@/types";

interface DiscussionInputProps {
  onSubmit: (content: string, mentions?: string[]) => Promise<void>;
  collaborators: CollaboratorUserInfo[];
  currentUserId: string;
  placeholder?: string;
}

export function DiscussionInput({
  onSubmit,
  collaborators,
  currentUserId,
  placeholder = "Message...",
}: DiscussionInputProps) {
  const [content, setContent] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const availableUsers = collaborators.filter((c) => c.id !== currentUserId);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowUserPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showUserPicker && pickerRef.current) {
      const el = pickerRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, showUserPicker]);

  const insertMention = useCallback(
    (user: CollaboratorUserInfo) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const cursorPos = textarea.selectionStart;
      const textBefore = content.substring(0, cursorPos);
      const textAfter = content.substring(cursorPos);
      const mention = `@${user.email} `;
      const newContent = textBefore + mention + textAfter;

      setContent(newContent);
      setShowUserPicker(false);

      setTimeout(() => {
        const newPos = cursorPos + mention.length;
        textarea.focus();
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    },
    [content],
  );

  const handleSend = async () => {
    if (!content.trim() || isSending) return;

    setIsSending(true);
    try {
      const mentionRegex = /@([^\s@]+)/g;
      const mentionedIds: string[] = [];
      let match;
      while ((match = mentionRegex.exec(content)) !== null) {
        const email = match[1];
        const user = collaborators.find((c) => c.email === email);
        if (user) {
          mentionedIds.push(user.id);
        }
      }

      await onSubmit(content.trim(), mentionedIds);
      setContent("");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showUserPicker && availableUsers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < availableUsers.length - 1 ? prev + 1 : 0));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : availableUsers.length - 1));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(availableUsers[selectedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowUserPicker(false);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [content, adjustTextareaHeight]);

  const canSubmit = Boolean(content.trim()) && !isSending;

  return (
    <div className="relative">
      {showUserPicker && availableUsers.length > 0 ? (
        <div
          ref={pickerRef}
          className="absolute bottom-full left-0 z-50 mb-2 max-h-48 w-64 overflow-y-auto rounded-lg border border-slate-600 bg-slate-800 shadow-xl"
        >
          <div className="border-b border-slate-700 px-3 py-2 text-xs text-slate-400">
            Select a user to mention
          </div>
          {availableUsers.map((user, index) => (
            <button
              key={user.id}
              type="button"
              data-index={index}
              onClick={() => insertMention(user)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                index === selectedIndex
                  ? "bg-violet-600 text-white"
                  : "text-slate-200 hover:bg-slate-700",
              )}
            >
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-600">
                  <User className="h-3 w-3 text-slate-300" />
                </div>
              )}
              <div className="min-w-0 flex flex-col">
                <span className="truncate font-medium">{user.full_name || user.email}</span>
                {user.full_name ? (
                  <span className="truncate text-xs text-slate-400">{user.email}</span>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {showUserPicker && availableUsers.length === 0 ? (
        <div
          ref={pickerRef}
          className="absolute bottom-full left-0 z-50 mb-2 w-64 rounded-lg border border-slate-600 bg-slate-800 shadow-xl"
        >
          <div className="px-3 py-4 text-center text-sm text-slate-400">
            No other organization members yet
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-col rounded-xl border border-white/10 bg-white/5 focus-within:border-blue-500/50 focus-within:ring-2 focus-within:ring-blue-500/20">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={2}
          disabled={isSending}
          className="min-h-[44px] resize-none border-0 bg-transparent px-4 py-3 text-sm text-white placeholder-gray-500 shadow-none focus:outline-none focus-visible:ring-0"
          style={{ maxHeight: "120px" }}
        />
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
          <IconButton
            type="button"
            size="sm"
            active={showUserPicker}
            onClick={() => setShowUserPicker(!showUserPicker)}
            title="Mention someone"
            aria-label="Mention someone"
          >
            <AtSign className="h-4 w-4" />
          </IconButton>
          <Button
            type="button"
            variant="default"
            size="icon"
            onClick={() => void handleSend()}
            disabled={!canSubmit}
            aria-label="Send message"
            className="ml-auto shrink-0"
          >
            {isSending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
